import React, { useEffect, useState } from 'react';
import {
    LuBanknote,
    LuCalendarClock,
    LuChartNoAxesCombined,
    LuClipboardCheck,
    LuCpu,
    LuFilePenLine,
    LuFolderKanban,
    LuLayers,
    LuLayoutDashboard,
    LuLibraryBig,
    LuListChecks,
    LuLogOut,
    LuMegaphone,
    LuPanelLeft,
    LuReceipt,
    LuScrollText,
    LuSquareCheck,
    LuTarget,
} from 'react-icons/lu';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { FeatureFlag, Role } from '@ndvr/auth-api';

import { CalendarIcon, HouseholdIcon, ProspectIcon } from '@/components/Icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuthContext } from '@/views/auth/Auth';
import { MARKETING_PATH } from '@/views/marketing/marketing-routes';
import { useActiveRoles } from 'views/admin/internalUserProfiles/role-queries';
import { logout } from 'views/auth/auth-queries';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { HorizontalLayout, IconCodeBlock, IconReports, Text, VerticalLayout } from 'widgets';

import { ClientBaseSelector } from './ClientBaseSelector';
import { NavGroup } from './NavGroup';
import { QuickActions } from './QuickActions';
import { Version } from './Version';

export const Navigation = () => {
    const { user } = useAuthContext();

    const { data: activeRoles } = useActiveRoles(user?.personOrgGUID || '');

    const roles = activeRoles?.map((role) => role.role) || [];
    const hasReportsAccess = roles.includes(Role.REPORTS_ACCESS);
    const hasIopsAccess = roles.includes(Role.IOPS_PORTAL_ACCESS);
    const hasSysAdminAccess = roles.includes(Role.SYSTEM_ADMIN_ACCESS);

    const isRmdTrackerEnabled = useFeatureFlag(FeatureFlag.PM_RMD_TRACKER);
    const isAccountListEnabled = useFeatureFlag(FeatureFlag.PM_ACCOUNT_LIST);
    const isBillingEnabled = useFeatureFlag(FeatureFlag.CC_BILLING);
    const isOnboardingEnabled = useFeatureFlag(FeatureFlag.ONBOARDING);

    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem('navigation-collapsed');
        return saved ? JSON.parse(saved) : false;
    });

    useEffect(() => {
        localStorage.setItem('navigation-collapsed', JSON.stringify(isCollapsed));
    }, [isCollapsed]);

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    return (
        <Container $isCollapsed={isCollapsed}>
            <Link to="/home" className="flex h-[70px] shrink-0 items-center justify-center px-2.5">
                <img
                    src="/logo-short.svg"
                    width={isCollapsed ? 40 : 50}
                    height={((isCollapsed ? 40 : 50) * 71) / 146}
                    style={{ width: isCollapsed ? 40 : 50, height: ((isCollapsed ? 40 : 50) * 71) / 146 }}
                    className="block shrink-0"
                    alt="Claro logo"
                />
            </Link>
            <Version compact={isCollapsed} showCurrentVersion={false} />

            {!isCollapsed && <ClientBaseSelector />}

            <MenuItem url="/home" label="Dashboard" icon={<LuLayoutDashboard />} isCollapsed={isCollapsed} />

            {!isCollapsed && <Separator />}

            <MenuItem url="/household" label="Clients" icon={<HouseholdIcon />} isCollapsed={isCollapsed} />
            <MenuItem url="/prospect" label="Prospects" icon={<ProspectIcon />} isCollapsed={isCollapsed} />
            {isOnboardingEnabled && (
                <MenuItem url="/onboarding" label="Onboarding" icon={<LuClipboardCheck />} isCollapsed={isCollapsed} />
            )}

            {!isCollapsed && <Separator />}

            <MenuItem url="/tasks" label="Tasks" icon={<LuSquareCheck />} isCollapsed={isCollapsed} />
            <MenuItem url="/calendar" label="Calendar" icon={<CalendarIcon />} isCollapsed={isCollapsed} />
            {hasReportsAccess && (
                <MenuItem url="/reports" label="Reports" icon={<IconReports />} isCollapsed={isCollapsed} />
            )}
            <MenuItem url="/opportunities" label="Opportunities" icon={<LuTarget />} isCollapsed={isCollapsed} />
            <MenuItem url={MARKETING_PATH} label="Marketing" icon={<LuMegaphone />} isCollapsed={isCollapsed} />

            {isBillingEnabled && (
                <>
                    {!isCollapsed && <Separator />}

                    <NavGroup label="Billing" icon={<LuReceipt />} isCollapsed={isCollapsed} storageKey="billing-open">
                        <MenuItem
                            url="/billing/billing-runs"
                            label="Billing Runs"
                            icon={<LuClipboardCheck />}
                            isCollapsed={isCollapsed}
                        />
                        <MenuItem
                            url="/billing/fee-schedules"
                            label="Fee Schedules"
                            icon={<LuScrollText />}
                            isCollapsed={isCollapsed}
                        />
                        <MenuItem
                            url="/billing/billing-groups"
                            label="Billing Groups"
                            icon={<LuLayers />}
                            isCollapsed={isCollapsed}
                        />
                    </NavGroup>
                </>
            )}

            {!isCollapsed && <Separator />}

            <NavGroup
                label="Portfolio Engine"
                icon={<LuFolderKanban />}
                isCollapsed={isCollapsed}
                storageKey="portfolio-tools-open"
            >
                {isAccountListEnabled && (
                    <MenuItem url="/accounts" label="All Accounts" icon={<LuListChecks />} isCollapsed={isCollapsed} />
                )}
                <MenuItem
                    url="/trading/investment-requests"
                    label="Investment Requests"
                    icon={<LuChartNoAxesCombined />}
                    isCollapsed={isCollapsed}
                />
                <MenuItem
                    url="/trading/withdrawals"
                    label="Withdrawals"
                    icon={<LuBanknote />}
                    isCollapsed={isCollapsed}
                />
                <MenuItem
                    url="/model-library"
                    label="Model Library"
                    icon={<LuLibraryBig />}
                    isCollapsed={isCollapsed}
                />
                {isRmdTrackerEnabled && (
                    <MenuItem
                        url="/rmd-tracker"
                        label="RMD Tracker"
                        icon={<LuCalendarClock />}
                        isCollapsed={isCollapsed}
                    />
                )}
                {hasIopsAccess && (
                    <MenuItem
                        url="/fund-asset-allocations"
                        label="Fund asset allocations"
                        icon={<LuLayers />}
                        isCollapsed={isCollapsed}
                    />
                )}
            </NavGroup>

            {!isCollapsed && <Separator />}

            <ControlButtons>
                <QuickActions isCollapsed={isCollapsed} />
                {hasIopsAccess && (
                    <MenuItem url="/dashboard" label="IOPS Portal" icon={<LuCpu />} isCollapsed={isCollapsed} />
                )}
                <MenuItem url="/preferences" label="Preferences" icon={<LuFilePenLine />} isCollapsed={isCollapsed} />
                {hasSysAdminAccess && (
                    <MenuItem url="/admin" label="System Admin" icon={<IconCodeBlock />} isCollapsed={isCollapsed} />
                )}

                <MenuItem onClick={logout} label="Log Out" icon={<LuLogOut />} isCollapsed={isCollapsed} />

                <ToggleButton $isCollapsed={isCollapsed} onClick={toggleSidebar}>
                    <LuPanelLeft />
                </ToggleButton>
            </ControlButtons>
        </Container>
    );
};

interface CollapsibleProps {
    $isCollapsed: boolean;
}

const Container = styled.nav<CollapsibleProps>`
    display: flex;
    flex-direction: column;
    width: ${({ $isCollapsed }) => ($isCollapsed ? '60px' : '255px')};
    flex-shrink: 0;
    border-right: 1px solid ${({ theme }) => theme.border.light};
    align-items: stretch;
    overflow-x: auto;
    background: ${({ theme }) => theme.nav.primaryBackground};
    color: ${({ theme }) => theme.text.white};
    scrollbar-width: none;
    transition: width 0.3s ease;
    position: relative;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const Separator = styled.hr`
    border-top: 1px solid ${({ theme }) => theme.border.separator};
    margin: 4px auto;
    width: calc(100% - 20px);
    flex-shrink: 0;
`;

const ControlButtons = styled(VerticalLayout)`
    flex-grow: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding-bottom: 16px;
`;

type MenuItemPropsBase = {
    label: string;
    icon: JSX.Element;
    disabled?: boolean;
    isCollapsed?: boolean;
};

type MenuItemProps =
    | ({ url: string; onClick?: never } & MenuItemPropsBase)
    | ({ url?: never; onClick: () => void } & MenuItemPropsBase);

const MenuItem = ({ url, onClick, label, icon, disabled = false, isCollapsed = false }: MenuItemProps) => {
    const { pathname } = useLocation();

    const isActive = (): boolean => {
        if (!url) {
            return false;
        }
        switch (url) {
            case '/owner':
                return pathname.startsWith('/owner') || pathname.includes('/owner/');
            case '/household':
                return pathname.startsWith(url) && !pathname.includes('/owner/');
            default:
                return pathname.startsWith(url);
        }
    };

    return isCollapsed ? (
        <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
                <StyledLink
                    to={url ?? '#'}
                    $disabled={disabled}
                    onClick={(e) => {
                        if (onClick) {
                            e.preventDefault();
                            onClick();
                        }
                    }}
                    $active={isActive()}
                    $isCollapsed={isCollapsed}
                >
                    <HorizontalLayout $alignItems="center" $gap={10}>
                        {icon}
                        {!isCollapsed && <Text $centered>{label}</Text>}
                    </HorizontalLayout>
                </StyledLink>
            </TooltipTrigger>
            <TooltipContent side="right">
                <p>{label}</p>
            </TooltipContent>
        </Tooltip>
    ) : (
        <StyledLink
            to={url ?? '#'}
            $disabled={disabled}
            onClick={(e) => {
                if (onClick) {
                    e.preventDefault();
                    onClick();
                }
            }}
            $active={isActive()}
            $isCollapsed={isCollapsed}
        >
            <HorizontalLayout $alignItems="center" $gap={10}>
                {icon}
                {!isCollapsed && <Text $centered>{label}</Text>}
            </HorizontalLayout>
        </StyledLink>
    );
};

const StyledLink = styled(Link)<{ $disabled: boolean; $active: boolean; $isCollapsed: boolean }>`
    color: white;
    text-decoration: none;
    flex-shrink: 0;
    margin-top: 2px;
    margin-left: 10px;
    margin-right: 10px;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 11px 10px;
    display: flex;
    align-items: center;
    position: relative;
    line-height: 1;
    user-select: none;

    svg {
        width: 18px;
        height: 18px;
    }

    ${({ $disabled }) =>
        $disabled &&
        `
        pointer-events: none;
        opacity: 0.3;
        `}

    ${({ $active, theme }) =>
        $active &&
        `
        color: ${theme.text.white};
        background: ${theme.nav.highlightBackground};
    `}

    ${({ $active, theme }) =>
        !$active &&
        `
        &:hover {
            background: ${theme.nav.highlightBackground};
        }
    `}

    ${({ $isCollapsed }) =>
        $isCollapsed &&
        `
        padding: 11px;
        justify-content: center;
    `}
`;

const ToggleButton = styled.button<CollapsibleProps>`
    background: transparent;
    border: none;
    padding: 11px;
    flex-shrink: 0;
    cursor: pointer;
    color: ${({ theme }) => theme.text.white};
    border-radius: 4px;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    margin: 2px auto 0;

    svg {
        width: 18px;
        height: 18px;
    }

    &:hover {
        background: ${({ theme }) => theme.nav.highlightBackground};
    }
`;
